import type { Auth } from 'firebase/auth';
import { type Firestore, doc, getDoc, setDoc } from 'firebase/firestore';

import { fundPolicySchema } from '../../core/fundPolicy/schema';
import {
  SettingsError,
  type SettingsPort,
} from '../../features/settings/application/ports/settingsPort';

/**
 * Firestore implementation of `SettingsPort` (blueprint §11.1).
 *
 * The document is validated against the canonical schema before it is written.
 * Firestore rules check who may write and that the author is honest; they
 * cannot check that a coverage tier is well-formed, and this policy is read by
 * every employee screen.
 */
export function createFirebaseSettingsAdapter(
  firestore: Firestore,
  auth: Auth,
): SettingsPort {
  return {
    async saveFundPolicy(policy): Promise<void> {
      const uid = auth.currentUser?.uid;
      if (!uid) throw new SettingsError('forbidden', 'يجب تسجيل الدخول أولًا.');

      /*
       * The author comes from the verified link, not from the caller (§4.2).
       *
       * The rules also require `updatedBy` to equal the caller's own employee
       * id, so a forged author is refused at the boundary as well — this read
       * is what lets an honest client satisfy it.
       */
      const link = await getDoc(doc(firestore, 'userAccounts', uid));
      const employeeId: unknown = link.data()?.['employeeId'];

      if (typeof employeeId !== 'string') {
        throw new SettingsError('forbidden', 'لا يوجد سجل موظف مرتبط بحسابك.');
      }

      const candidate = {
        ...policy,
        updatedAt: new Date().toISOString(),
        updatedBy: employeeId,
      };

      const parsed = fundPolicySchema.safeParse(candidate);
      if (!parsed.success) {
        throw new SettingsError(
          'invalid',
          parsed.error.issues[0]?.message ?? 'الإعدادات غير صالحة.',
        );
      }

      try {
        await setDoc(doc(firestore, 'fundPolicy', 'current'), parsed.data);
      } catch (error) {
        const code =
          typeof error === 'object' && error !== null && 'code' in error
            ? String(error.code)
            : '';

        throw new SettingsError(
          code.includes('permission-denied') ? 'forbidden' : 'unknown',
          code.includes('permission-denied')
            ? 'لا تملك صلاحية تعديل إعدادات الصندوق.'
            : 'تعذّر حفظ الإعدادات.',
        );
      }
    },
  };
}
